import { useCallback, useState } from 'react'
import type { InstallmentType } from './mortgage'

interface ShareableState {
  propertyPrice: number
  downPaymentPercent: number
  loanYears: number
  wiborRate: number
  bankMargin: number
  monthlyOverpayment: number
  monthlyRent: number
  installmentType: InstallmentType
}

const PARAM_MAP = {
  propertyPrice: 'p',
  downPaymentPercent: 'd',
  loanYears: 'y',
  wiborRate: 'w',
  bankMargin: 'm',
  monthlyOverpayment: 'o',
  monthlyRent: 'r',
  installmentType: 't',
} as const

export function encodeShareUrl(state: ShareableState): string {
  const params = new URLSearchParams()
  params.set(PARAM_MAP.propertyPrice, String(state.propertyPrice))
  params.set(PARAM_MAP.downPaymentPercent, String(state.downPaymentPercent))
  params.set(PARAM_MAP.loanYears, String(state.loanYears))
  params.set(PARAM_MAP.wiborRate, String(state.wiborRate))
  params.set(PARAM_MAP.bankMargin, String(state.bankMargin))
  if (state.monthlyOverpayment > 0) {
    params.set(PARAM_MAP.monthlyOverpayment, String(state.monthlyOverpayment))
  }
  if (state.monthlyRent > 0) {
    params.set(PARAM_MAP.monthlyRent, String(state.monthlyRent))
  }
  if (state.installmentType !== 'equal') {
    params.set(PARAM_MAP.installmentType, state.installmentType)
  }
  return `${window.location.origin}/?${params.toString()}`
}

export function decodeShareUrl(): Partial<ShareableState> | null {
  const params = new URLSearchParams(window.location.search)
  if (params.size === 0) return null

  const result: Partial<ShareableState> = {}

  const p = params.get(PARAM_MAP.propertyPrice)
  if (p) result.propertyPrice = Number(p)

  const d = params.get(PARAM_MAP.downPaymentPercent)
  if (d) result.downPaymentPercent = Number(d)

  const y = params.get(PARAM_MAP.loanYears)
  if (y) result.loanYears = Number(y)

  const w = params.get(PARAM_MAP.wiborRate)
  if (w) result.wiborRate = Number(w)

  const m = params.get(PARAM_MAP.bankMargin)
  if (m) result.bankMargin = Number(m)

  const o = params.get(PARAM_MAP.monthlyOverpayment)
  if (o) result.monthlyOverpayment = Number(o)

  const r = params.get(PARAM_MAP.monthlyRent)
  if (r) result.monthlyRent = Number(r)

  const t = params.get(PARAM_MAP.installmentType)
  if (t === 'equal' || t === 'decreasing') result.installmentType = t

  return result
}

export function useShareUrl() {
  const [copied, setCopied] = useState(false)

  const share = useCallback(async (state: ShareableState) => {
    const url = encodeShareUrl(state)

    // Use native Web Share API only on mobile/tablet (touch devices)
    // Desktop browsers show a clunky context menu that doesn't give feedback
    const isMobile = 'ontouchstart' in window || navigator.maxTouchPoints > 0
    if (isMobile && navigator.share) {
      try {
        await navigator.share({ url })
        return
      } catch {
        // User cancelled — fall through to clipboard
      }
    }

    // Copy to clipboard (desktop, or mobile fallback)
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard not available — ignore
    }
  }, [])

  return { share, copied }
}
